import { supabase } from './supabase';
import { 
  queryCache, 
  getPlayerStatsCacheKey, 
  getLeagueAveragesCacheKey,
  getWeeklyAveragesCacheKey,
  getLanePerformanceCacheKey,
  getGamePositionCacheKey,
  getPlayerListCacheKey
} from './queryCache';
import { measureQueryPerformance } from './performanceMonitoring';
import { getCurrentSeasonYear } from '../config/season';
import type { PlayerStats, WeeklyAverage, Player } from '../types';

/**
 * Optimized player stats fetching with caching
 */
export async function fetchPlayerStatsOptimized(
  bowlerId: number, 
  seasonYear: number = getCurrentSeasonYear()
): Promise<PlayerStats> {
  const cacheKey = getPlayerStatsCacheKey(bowlerId, seasonYear);
  
  // Try to get from cache first
  const cached = queryCache.get<PlayerStats>(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from database with performance monitoring
  const [statsResult, weeklyResult] = await Promise.all([
    measureQueryPerformance(
      `get_player_statistics_${bowlerId}`,
      async () => await supabase.rpc('get_player_statistics', {
        target_bowler_id: bowlerId,
        season_year: seasonYear,
      })
    ),
    measureQueryPerformance(
      `get_weekly_averages_${bowlerId}`,
      async () => await supabase.rpc('get_weekly_averages', {
        target_bowler_id: bowlerId,
        season_year: seasonYear,
      })
    )
  ]);

  if ((statsResult as any).error) throw (statsResult as any).error;
  if ((weeklyResult as any).error) throw (weeklyResult as any).error;

  // Transform weekly data
  const weeklyData = (weeklyResult as any).data || [];
  const weeklyAverages = weeklyData.map((week: any): WeeklyAverage => ({
    week_date: week.week_date,
    week_number: week.week_number,
    average_score: week.average_score,
    games_played: week.games_played,
    total_pins: week.total_pins,
  }));

  // Calculate current average from most recent week data
  const mostRecentWeek = weeklyAverages.reduce((latest: WeeklyAverage, current: WeeklyAverage) => 
    current.week_number > latest.week_number ? current : latest,
    weeklyAverages[0] || { week_number: 0, total_pins: 0, games_played: 0, week_date: '', average_score: 0 }
  );

  const statsData = (statsResult as any).data || [];
  const currentAverage = mostRecentWeek && mostRecentWeek.games_played > 0 
    ? mostRecentWeek.total_pins / mostRecentWeek.games_played 
    : statsData[0]?.current_average || 0;

  const playerStats: PlayerStats = {
    current_average: currentAverage,
    total_pins: mostRecentWeek?.total_pins || statsData[0]?.total_pins || 0,
    total_games: mostRecentWeek?.games_played || statsData[0]?.total_games || 0,
    handicap: statsData[0]?.handicap || 0,
    high_scratch_game: statsData[0]?.high_scratch_game || 0,
    high_scratch_series: statsData[0]?.high_scratch_series || 0,
    high_handicap_game: statsData[0]?.high_handicap_game || 0,
    high_handicap_series: statsData[0]?.high_handicap_series || 0,
    most_improved: statsData[0]?.most_improved || 0,
    weekly_averages: weeklyAverages,
  };

  // Cache the result for 5 minutes
  queryCache.set(cacheKey, playerStats, 5 * 60 * 1000);

  return playerStats;
}

/**
 * Optimized league averages fetching with caching
 */
export async function fetchLeagueAveragesOptimized(
  seasonYear: number = getCurrentSeasonYear(),
  excludeBowlerId?: number
): Promise<number> {
  const cacheKey = getLeagueAveragesCacheKey(seasonYear, excludeBowlerId);
  
  // Try to get from cache first
  const cached = queryCache.get<number>(cacheKey);
  if (cached !== null) {
    return cached;
  }

  // Fetch from database
  const { data, error } = await supabase.rpc('get_league_average', {
    season_year: seasonYear,
    exclude_bowler_id: excludeBowlerId || null,
  });

  if (error) throw error;

  const leagueAverage = data || 0;

  // Cache the result for 10 minutes (league averages change less frequently)
  queryCache.set(cacheKey, leagueAverage, 10 * 60 * 1000);

  return leagueAverage;
}

/**
 * Optimized player list fetching with caching
 */
export async function fetchPlayersOptimized(): Promise<Player[]> {
  const cacheKey = getPlayerListCacheKey();
  
  // Try to get from cache first
  const cached = queryCache.get<Player[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from database using RPC function
  const { data, error } = await supabase.rpc('get_players');

  if (error) {
    console.error('Supabase error fetching players:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
    });
    throw error;
  }

  const players = data || [];

  // Cache the result for 30 minutes (player list changes infrequently)
  queryCache.set(cacheKey, players, 30 * 60 * 1000);

  return players;
}

/**
 * Optimized lane performance fetching with caching
 */
export async function fetchLanePerformanceOptimized(
  bowlerId: number,
  seasonYear: number = getCurrentSeasonYear()
): Promise<any[]> {
  const cacheKey = getLanePerformanceCacheKey(bowlerId, seasonYear);
  
  // Try to get from cache first
  const cached = queryCache.get<any[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from database
  const { data, error } = await supabase.rpc('get_lane_performance', {
    target_bowler_id: bowlerId,
    season_year: seasonYear,
  });

  if (error) throw error;

  const lanePerformance = data || [];

  // Cache the result for 5 minutes
  queryCache.set(cacheKey, lanePerformance, 5 * 60 * 1000);

  return lanePerformance;
}

/**
 * Optimized game position performance fetching with caching
 */
export async function fetchGamePositionPerformanceOptimized(
  bowlerId: number,
  seasonYear: number = getCurrentSeasonYear()
): Promise<any[]> {
  const cacheKey = getGamePositionCacheKey(bowlerId, seasonYear);
  
  // Try to get from cache first
  const cached = queryCache.get<any[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from database
  const { data, error } = await supabase.rpc('get_game_position_performance', {
    target_bowler_id: bowlerId,
    season_year: seasonYear,
  });

  if (error) throw error;

  const gamePositionPerformance = data || [];

  // Cache the result for 5 minutes
  queryCache.set(cacheKey, gamePositionPerformance, 5 * 60 * 1000);

  return gamePositionPerformance;
}

/**
 * Invalidate cache for a specific player when their data changes
 */
export function invalidatePlayerCache(bowlerId: number, seasonYear?: number): void {
  const year = seasonYear || getCurrentSeasonYear();
  
  queryCache.delete(getPlayerStatsCacheKey(bowlerId, year));
  queryCache.delete(getWeeklyAveragesCacheKey(bowlerId, year));
  queryCache.delete(getLanePerformanceCacheKey(bowlerId, year));
  queryCache.delete(getGamePositionCacheKey(bowlerId, year));
  
  // Also invalidate league averages that might include this player
  queryCache.delete(getLeagueAveragesCacheKey(year));
  queryCache.delete(getLeagueAveragesCacheKey(year, bowlerId));
}

/**
 * Invalidate all cache entries
 */
export function invalidateAllCache(): void {
  queryCache.clear();
}